/**
 * Ryan O'Dowd
 * 2022-12-26
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  RSAA,
  getJSON,
} from 'redux-api-middleware';
import {
  GoogleOneTapSignIn,
} from '@react-native-google-signin/google-signin';
import {
  Platform,
} from 'react-native';
import fb from '@utils/firebase';
import globals from '@utils/globals';
import jwt from '@utils/utilities/jwt';
import {
  v4 as uuidv4,
} from 'uuid';

export const SET_CHAT_MESSAGES = 'SET_CHAT_MESSAGES';
export const FETCH_REQUEST = 'FETCH_REQUEST';
export const FETCH_ERROR = 'FETCH_ERROR';
export const REMOVE_FETCH = 'REMOVE_FETCH';
export const UPDATE_FETCH = 'UPDATE_FETCH';
export const UPDATE_LOADING_STATE = 'UPDATE_LOADING_STATE';
export const NOT_LOADING = 'NOT_LOADING';
export const SET_WHATS_NEW_LAST_READ_TIMESTAMP = 'SET_WHATS_NEW_LAST_READ_TIMESTAMP';
export const SET_WHATS_NEW_LAST_READ_VERSION = 'SET_WHATS_NEW_LAST_READ_VERSION';
export const SET_WHATS_NEW_NOTES = 'SET_WHATS_NEW_NOTES';
export const SET_FEEDBACK_DRAFT_TEXT = 'SET_FEEDBACK_DRAFT_TEXT';
export const SET_LIGHT_DARK_MODE = 'SET_LIGHT_DARK_MODE';
export const LOGOUT = 'LOGOUT';
export const SET_OAK_USER = 'SET_OAK_USER';
export const SET_PROFILE_PICTURE_URL = 'SET_PROFILE_PICTURE_URL';
export const CLEAN_PROFILE_PICTURE_URLS = 'CLEAN_PROFILE_PICTURE_URLS';

export const JSON_HEADERS = {
  Accept: 'application/json, text/plain, */*',
  'Content-Type': 'application/json',
};

// fetch status
export const FETCH_REQUEST_OBJECT = {
  status: 'IN_FLIGHT',
};
export const FETCH_SUCCESS_OBJECT = {
  status: 'SUCCESS',
};
export const FETCH_ERROR_OBJECT = {
  status: 'ERROR',
  message: '<no errror provided...check reducer for FETCH_ERROR>',
};

// methods
export const GET = 'GET';
export const POST = 'POST';
export const PATCH = 'PATCH';
export const PUT = 'PUT';
export const DELETE = 'DELETE';

export const getFetchTypes = (endpoint, successType, skipMessage = false, errorCallback = undefined, actionCreatorName = undefined) => { // @TODO: this seems needlessly complex
  const requestId = uuidv4();
  return [
    {
      type: FETCH_REQUEST,
      payload: {endpoint},
      meta: {
        requestId,
        endpoint,
        actionCreatorName,
      },
    },
    {
      type: successType,
      payload: (__action, __state, res) => {
        return res.json().then((data) => data);
      },
      meta: (action, __state, res) => {
        delete action['@@redux-api-middleware/RSAA'];
        if (res) {
          return {
            responseStatus: res.status,
            responseStatusText: res.statusText,
            requestId,
            endpoint,
            actionCreatorName,
            ...action, // @TODO: probably shouldn't spread this
          };
        } else {
          return {
            status: 'Network request failed',
          };
        }
      },
    },
    {
      type: FETCH_ERROR,
      payload: (__action, __state, res) => {
        const defaultMessage = `${res.status} - ${res.statusText}`;
        // @TODO: make call to crashlytics with res object (include request if possible too...at least endpoint)
        if (res) {
          return getJSON(res).then((json) => {
            if (json.message) {
              return json;
            }
            return {
              message: defaultMessage,
              ...json,
            };
          }).catch(() => {
            return {
              message: defaultMessage,
            };
          });
        } else {
          return {
            message: '',
          };
        }
      },
      meta: (__action, __state, res) => {
        if (res) {
          return { // @TODO: this shape should be the same as success
            responseStatus: res.status,
            responseStatusText: res.statusText,
            requestId,
            endpoint,
            skipMessage,
            errorCallback,
            actionCreatorName,
          };
        } else {
          return {
            requestId,
            endpoint,
            skipMessage,
            errorCallback,
          };
        }
      },
    },
  ];
};

export const getRequestHeaders = async (ignoreAuth = false, userId = null, onError = null, dispatch = null) => { // @TODO: this shouldn't be exported and prepend with _
  const userMasqueradingAs = Platform.OS === 'web' ? sessionStorage.getItem('masqueradeAsUserId') : null; // can't get from redux because of race condition or circular imports or something // @TODO: confirm this...should work now

  const headers = {
    'X-version': globals.appVersionNumber,
    'X-platform': Platform.OS,
    'X-app-name': globals.appName,
  } as any;

  if (userId) {
    headers['X-user-id'] = userId; // @NOTE: send in case jwt isn't included in this call
  }
  if (userMasqueradingAs) {
    headers['X-masquerade-user-id'] = userMasqueradingAs;
  }

  if (!ignoreAuth) {
    try {
      const currToken = await jwt.getIdTokenSafely();
      if (currToken) {
        headers.Authorization = currToken;
      } else {
        throw new Error('no exception thrown, but no jwt'); // @TODO: don't need to log to crashlytics if onError is passed in
      }
    } catch (error) {
      if (onError) {
        onError(dispatch);
      }
      switch (error.code) {
      case 'auth/network-request-failed': {
        // @TODO: don't need to log to crashlytics
        break;
      }
      /* @TODO: might not be safe to ignore this case...could be that user needs to be logged out and re sign in: "The user's credential is no longer valid. The user must sign in again."
      case 'auth/user-token-expired': {
        break;
      }
      */
      case 'auth/no-current-user': {
        break;
      }
      default:
        fb.recordError(fb.crashlytics, error);
        break;
      }

      // prevent request from being made since it will be a 401 with the jwt
      throw new Error('auth error thrown', {cause: error});
    }
  }

  return headers;
};

export function createApiAction(rsaaAttrs, {
  ignoreAuth=false,
  loadingStateToTrack=null,
  loadingStateMetadata=null,
  callback=null,
  onError=null,
}={}) {
  return async (dispatch, getState) => {
    if (loadingStateToTrack) {
      dispatch(updateLoadingState(loadingStateToTrack, true, loadingStateMetadata));
    }

    try {
      const userId = getState().oakUser?.id;
      const headers = await getRequestHeaders(ignoreAuth, userId, onError, dispatch);

      const response = await dispatch({
        [RSAA]: {
          ...rsaaAttrs,
          headers: {...headers, ...rsaaAttrs.headers},
        },
      });

      await callback?.(dispatch, response, getState);
    } catch (error) {
      fb.recordError(fb.crashlytics, error);
      return { // @TODO: is this the best thing to return?
        type: 'TODO_FOO_BAR',
        payload: error.message,
        error: true,
      };
    } finally {
      if (loadingStateToTrack) {
        dispatch(updateLoadingState(loadingStateToTrack, false));
      }
    }
  };
}

export function logout() {
  return async (dispatch) => {
    try {
      await fb.signOut(fb.auth);

      if (Platform.OS !== 'web') {
        await GoogleOneTapSignIn.signOut();
      }
    } catch (error) {
      if (!['auth/no-current-user'].includes(error.code)) {
        fb.recordError(fb.crashlytics, error);
      }
    } finally {
      dispatch({type: LOGOUT});
    }
  };
}

export function updateLoadingState(resource, isLoading, metadata = undefined) {
  return {
    type: UPDATE_LOADING_STATE,
    resource,
    isLoading,
    metadata,
  };
}

export function fetchChatMessages() {
  const endpoint = `${globals.apiUrl}/chats`;
  return createApiAction({
    endpoint,
    method: GET,
    types: getFetchTypes(endpoint, SET_CHAT_MESSAGES),
  });
}

export function sendChatMessage(messageBody) { // @TODO: add support for attachments
  const formData = new FormData();
  formData.append('message_body', messageBody);
  const endpoint = `${globals.apiUrl}/chats`;
  return createApiAction({
    endpoint,
    method: POST,
    body: formData,
    types: getFetchTypes(endpoint, SET_CHAT_MESSAGES),
  });
}

export function setMessageReaction(threadUserId, chatId, reactionName) {
  const formData = new FormData();
  formData.append('thread_user_id', threadUserId);
  formData.append('reaction_name', reactionName);
  const endpoint = `${globals.apiUrl}/chats/${chatId}/reaction`;
  return createApiAction({
    endpoint,
    method: PATCH,
    body: formData,
    types: getFetchTypes(endpoint, SET_CHAT_MESSAGES),
  });
}

export function markAllMessagesRead() {
  const endpoint = `${globals.apiUrl}/chats/all_read`;
  return createApiAction({
    endpoint,
    method: POST,
    types: getFetchTypes(endpoint, SET_CHAT_MESSAGES),
  });
}

export function setWhatsNewLastReadTimestamp(timestamp) {
  return {
    type: SET_WHATS_NEW_LAST_READ_TIMESTAMP,
    timestamp,
  };
}

export function setWhatsNewLastReadVersion(version) {
  return {
    type: SET_WHATS_NEW_LAST_READ_VERSION,
    version,
  };
}

export function sendContactFormMessage(firstName, lastName, emailAddress, howDidYouHearAboutUs, message) {
  const formData = new FormData();
  formData.append('first_name', firstName);
  formData.append('last_name', lastName);
  formData.append('email', emailAddress);
  formData.append('how_did_you_hear_about_us', howDidYouHearAboutUs);
  formData.append('message', message);
  const endpoint = `${globals.apiUrl}/contact`;
  return createApiAction({ // @TODO: if this fails, need to save this somewhere to retry again when we're back online (or at least alert user)...this is web, so offline isn't as likely
    endpoint,
    method: POST,
    body: formData,
    types: getFetchTypes(endpoint, ''),
  }, {ignoreAuth: true});
}

export function fetchWhatsNew() {
  const endpoint = `${globals.apiUrl}/whats_new`;
  return createApiAction({
    endpoint,
    method: GET,
    types: getFetchTypes(endpoint, SET_WHATS_NEW_NOTES),
  });
}

export function setFeedbackChatDraftText(feedbackChatDraftText) {
  return {
    type: SET_FEEDBACK_DRAFT_TEXT,
    feedbackChatDraftText,
  };
}

export function saveFcmToken(fcmToken, deviceId, platform) {
  const formData = new FormData();
  formData.append('fcm_token', fcmToken);
  formData.append('device_id', deviceId);
  formData.append('platform', platform);
  const endpoint = `${globals.apiUrl}/user/fcm_tokens`;
  return createApiAction({
    endpoint,
    method: PUT,
    body: formData,
    types: getFetchTypes(endpoint, ''),
  }, {loadingStateToTrack: 'saveFcmToken'});
}

export function setLightDarkMode(lightDarkMode) {
  return {
    type: SET_LIGHT_DARK_MODE,
    lightDarkMode,
  };
}

export function apiDeleteAccount() {
  const endpoint = `${globals.apiUrl}/delete_account`;
  return createApiAction({
    endpoint,
    method: POST,
    types: getFetchTypes(endpoint, ''),
  }, {callback: async (dispatch) => dispatch(logout())});
}

export function fetchProfilePictureUrl(usernameOrSchoolId, isSchoolLogo = false) { // @TODO: rename to allow for school logos
  const endpoint = isSchoolLogo ? `${globals.apiUrl}/schools/${usernameOrSchoolId}/logo_url` : `${globals.apiUrl}/users/${usernameOrSchoolId}/profile_picture_url`;
  return createApiAction({
    endpoint,
    method: GET,
    types: getFetchTypes(endpoint, SET_PROFILE_PICTURE_URL),
  });
}

export function cleanProfilePictureUrls() {
  return {
    type: CLEAN_PROFILE_PICTURE_URLS,
  };
}
